import { useEffect, useState, useRef } from "react";
import arrayUtils from "utils/array.util";
import { on } from "infrastructure/eventHub";

/**
 * useSyncedResource
 *
 * Hook genérico para consumir recursos sincronizados.
 *
 * Combina:
 * - lectura desde cache local (IndexedDB / memoria)
 * - sincronización remota controlada por SyncEngine
 * - actualización reactiva ante cambios del repository
 * - registro de visibilidad (scope + syncParams)
 *
 * @param {Object} options
 *
 * @param {string|null} options.scope
 *   Identificador del tipo de recurso (SCOPE.CHAT.LINES, SCOPE.CHAT.CONVERSATIONS, etc.)
 *
 * @param {Object|null} options.syncParams
 *   Parámetros que identifican la instancia del recurso visible y son utilizados dentro de la función encargada de la sincronización remota
 *   (ej: { lineId }, { lineId, phone }, {}).
 *
 * @param {Array} [options.key=['all']]
 *   Clave utilizada para cache local dentro del repository.
 *
 * @param {Function} options.cacheFn
 *   Función que obtiene datos desde cache local.
 *
 * @param {Function} options.syncFn
 *   Función que ejecuta la sincronización remota.
 *   Recibe: { params, cached }
 *
 * @param {Object|null} [options.repository]
 *   Repositorio asociado. Si se provee, el hook escucha eventos
 *   de actualización para refrescar la UI.
 *
 * @param {Array} [options.deps=[]]
 *   Dependencias de React que controlan cuándo se vuelve a cargar el recurso.
 *
 * @param {Object|null} [options.syncEngine]
 *   Servicio de sincronización. Se usa para registrar y limpiar scope activos.
 *
 * @returns {{
 *   data: Array,
 *   loading: boolean,
 *   setData: Function,
 *   refresh: Function
 * }}
 */
export function useSyncedResource({enabled = true, scope = null, key = "(all)", cacheFn, syncParams = null, syncFn, syncEngine = null, repository = null, repositories = null, deps = []}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const syncPromiseRef = useRef(null);
  const cancelledRef = useRef(false);

  async function load() {
    if (!enabled) return;

    const cached = await cacheFn({
      scope,
      key
    });
    if (!cancelledRef.current) setData(cached);

    if (!syncPromiseRef.current) {
      syncPromiseRef.current = syncFn({
        syncEngine,
        scope,
        repository,
        repositories,
        params: syncParams,
        cached,
        onCall: () => setLoading(true),
        onFinally: () => setLoading(false),
      }).catch((error) => {
        console.log(error)
      }).finally(() => {
        syncPromiseRef.current = null;
      });
    }

    await syncPromiseRef.current;
  }

  useEffect(() => {
    if (!enabled || !syncEngine || !scope || !key || !syncFn) return;

    syncEngine.activeScopes.set(scope, key, { syncEngine, repository, repositories, syncFn, syncParams });
    return () => {
      syncEngine.activeScopes.remove(scope, key);
    };
  }, [enabled, syncEngine, scope, key, syncFn]);

  useEffect(() => {
    if (!enabled || !repository || !scope) return;

    const unsub = on("repository", repository.storeName, "updated", async () => {
      repository.clearCache(scope); // Limpia todo el cache para evitar guardar cache de un componente que en este momento no esté visible
      const fresh = await cacheFn({
        scope,
        key
      });
      if (!cancelledRef.current) {
        setData(prev => {
          if (arrayUtils.shallowEqual(prev, fresh)) {
            return prev;
          }
          return fresh;
        });
      }
    });

    return unsub;
  }, [enabled, repository, scope, key, ...deps]);

  useEffect(() => {
    cancelledRef.current = false;
    if (enabled) {
      load();
    } else {
      setData([]);
    }
    return () => { cancelledRef.current = true; };
  }, [enabled, ...deps]);


  return { data, loading };
}
